import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { deviceName } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow } from "@/components/common";
import { CodeInput } from "@/pages/public/Login";

export function Join() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setMe } = useAuth();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ invitation_code: (params.get("code") || "").toUpperCase(), name: "", email: "", phone: "", code: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (f.invitation_code.length < 8) {
      setInv(null);
      return;
    }
    api.get(`/public/invitations/${f.invitation_code}`).then(({ data }) => {
      setInv(data);
      if (data.valid) setF((x) => ({ ...x, name: x.name || data.invitee_name, email: x.email || data.invitee_email }));
    }).catch(() => setInv({ valid: false }));
  }, [f.invitation_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.code !== f.confirm) return setError("Les deux codes ne correspondent pas.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { ...f, device: deviceName() });
      setMe(data);
      nav("/candidature", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const input = (k, label, props = {}) => (
    <label className="block"><span className="mr-label">{label}</span><input data-testid={`join-${k}-input`} className="mr-input" value={f[k]} onChange={set(k)} {...props} /></label>
  );
  return (
    <section className="max-w-md mx-auto px-5 py-16 lg:py-24 mr-fade-up">
      <Eyebrow className="mb-4">Invitation</Eyebrow>
      <h1 className="mr-h2 sm:text-4xl">Créer mon compte</h1>
      <p className="text-mr-text2 mt-3 mb-8 text-[15px]">Saisissez votre code d'invitation, puis choisissez votre code personnel à 6 chiffres : il vous servira à vous connecter.</p>
      <form onSubmit={submit} className="mr-card p-6 space-y-4">
        <label className="block"><span className="mr-label">Code d'invitation</span>
          <input data-testid="join-invitation-code-input" className="mr-input tracking-[0.3em] uppercase tabular" maxLength={8} value={f.invitation_code}
            onChange={(e) => setF({ ...f, invitation_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} autoFocus />
        </label>
        {inv && (inv.valid
          ? <p className="text-sm text-mr-accent flex items-center gap-2" data-testid="join-invitation-valid"><Check size={16} /> Invitation de {inv.sponsor_name}</p>
          : <p className="text-sm text-mr-danger" data-testid="join-invitation-invalid">Code invalide, expiré ou déjà utilisé.</p>)}
        {input("name", "Nom et prénom")}
        {input("email", "E-mail", { type: "email", autoComplete: "email" })}
        {input("phone", "Téléphone", { inputMode: "tel", placeholder: "07 00 00 00 00" })}
        <label className="block"><span className="mr-label">Code personnel (6 chiffres)</span><CodeInput testId="join-code-input" value={f.code} onChange={(v) => setF({ ...f, code: v })} /></label>
        <label className="block"><span className="mr-label">Confirmer le code</span><CodeInput testId="join-confirm-input" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} /></label>
        <button data-testid="join-submit-button" className="mr-btn-primary w-full" disabled={busy || !inv?.valid || f.code.length !== 6}>{busy ? "Création…" : "Créer mon compte"} <ArrowRight size={16} /></button>
        {error && <p className="text-sm text-mr-danger" data-testid="join-error">{error}</p>}
      </form>
      <p className="text-sm text-mr-muted mt-6">Déjà inscrit ? <Link to="/connexion" className="text-mr-accent hover:underline">Se connecter</Link></p>
    </section>
  );
}

export function ResetCode() {
  const [params] = useSearchParams();
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (code !== confirm) return setError("Les deux codes ne correspondent pas.");
    try {
      const { data } = await api.post("/auth/reset-code", { token: params.get("token") || "", code });
      setDone(data.message);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <section className="max-w-md mx-auto px-5 py-16 lg:py-24 mr-fade-up">
      <Eyebrow className="mb-4">Sécurité</Eyebrow>
      <h1 className="mr-h2 sm:text-4xl">Nouveau code personnel</h1>
      {done ? (
        <div className="mr-card p-6 mt-8 space-y-4" data-testid="reset-done"><p className="text-mr-text2">{done}</p><Link to="/connexion" className="mr-btn-primary w-full">Se connecter</Link></div>
      ) : (
        <form onSubmit={submit} className="mr-card p-6 mt-8 space-y-4">
          <label className="block"><span className="mr-label">Nouveau code (6 chiffres)</span><CodeInput testId="reset-code-input" value={code} onChange={setCode} autoFocus /></label>
          <label className="block"><span className="mr-label">Confirmer</span><CodeInput testId="reset-confirm-input" value={confirm} onChange={setConfirm} /></label>
          <button data-testid="reset-submit-button" className="mr-btn-primary w-full" disabled={code.length !== 6}>Enregistrer</button>
          {error && <p className="text-sm text-mr-danger" data-testid="reset-error">{error}</p>}
        </form>
      )}
    </section>
  );
}
